import { useParams, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ConfirmationModalV2 } from 'components/v2/confirmation-modal/confirmation-modal-v2';
import { useConfirmationVisit, useVisitState } from 'store/visits/hooks';
import { clearConfirmationCache } from 'store/visits/actions';
import { OwnerParams } from 'shared/types';
import { buildEditVisitPath, buildCancelVisitPath } from 'utils/route-helpers';
import { canCancelVisit, canEditVisit } from 'utils/visit-status-rules';
import { Step } from 'components/visit-form/types';
import { useUiMetadata } from 'store/ui-metadata/hooks';
import { VisitDetailsNotFound } from './visit-details-modal-states';

export const VisitConfirmationPageV2 = (): JSX.Element => {
  const { visitId } = useParams<{ visitId: string }>();
  const ownerParams = useParams<OwnerParams>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Try to get visit from confirmationCache first (for newly created visits with all visitors)
  const cachedVisit = useConfirmationVisit(visitId || '');

  // Fallback to fetching visit if not in cache (for existing visits clicked from list)
  // Pass true to only fetch if data is missing from regular state
  const visitState = useVisitState(visitId || '', true);

  // Use cached visit if available (preserves multi-visitor data), otherwise use fetched visit
  const visit = cachedVisit || visitState.data;

  const metadata = useUiMetadata();
  const editVisitsEnabled = metadata?.ui_metadata.visit_editing_enabled || false;
  const cancelVisitsEnabled = metadata?.ui_metadata.cancel_visits_enabled || false;

  const handleClose = useCallback(() => {
    if (visitId) {
      dispatch(clearConfirmationCache({ visitId }));
    }
    navigate('..', { relative: 'route' });
  }, [navigate, visitId, dispatch]);

  const handleEditDate = useCallback(() => {
    if (!visitId || !ownerParams.ownerId || !ownerParams.ownerType) {
      return;
    }
    const editPath = buildEditVisitPath(ownerParams as OwnerParams, visitId, Step.Date);
    navigate(editPath);
  }, [navigate, visitId, ownerParams]);

  const handleCancelVisit = useCallback(() => {
    navigate(buildCancelVisitPath(ownerParams as OwnerParams, visitId), {
      state: {
        visitors: visit?.visitors,
        cancelScope: visit?.is_group_visit ? 'group' : 'single',
      },
    });
  }, [navigate, ownerParams, visitId, visit?.is_group_visit, visit?.visitors]);

  if (!visitId) {
    return <div>Visit ID not found</div>;
  }

  // A null visit is no longer "render nothing" — ConfirmationModalV2 now holds a skeleton in its frame
  // so the modal opens once. That is right while a fetch is in flight and wrong once one has failed: a
  // failed fetch leaves `data` null and `loading` false, which would otherwise park this route on a
  // skeleton forever with no way to tell the user why.
  //
  // Only an outright error counts as missing, matching the detail route: before the fetch effect runs
  // `loading` is still false and `data` still null, so treating "no visit" as not-found would flash the
  // error on every arrival. A cached visit is the post-create happy path and never waits on a fetch, so
  // it short-circuits this entirely.
  if (!cachedVisit && visitState.error) {
    return <VisitDetailsNotFound onClose={handleClose} />;
  }

  return (
    // Keeps its fixed confirmation heading — it reports the outcome of a create, not the visit's state —
    // but the actions still have to respect that state, or a re-opened link offers to cancel a visit that
    // has already been cancelled.
    <ConfirmationModalV2
      visit={visit}
      onClose={handleClose}
      onEditDate={editVisitsEnabled && canEditVisit(visit?.visit_status) ? handleEditDate : undefined}
      onCancelVisit={cancelVisitsEnabled && canCancelVisit(visit?.visit_status) ? handleCancelVisit : undefined}
    />
  );
};
